/**
 * frontend/src/pages/valuation/_useTuningPreview.ts
 *
 * The debounced live recompute/preflight loop for ValuationTuning.tsx, split
 * out under docs/FILE_SIZE_POLICY.md (the screen file crossed the 400-line
 * hard-stop threshold once D2/D3/D6 draft wiring landed). Owns exactly the
 * pair of calls and the state they fill — nothing about industry, earnings,
 * or knobs, which stay on the screen and feed in as `body`.
 *
 * `/recompute` never clamps; `/preflight` rides the same debounce so the
 * blocker list can never describe a different set of inputs than the number
 * on screen. D2 — a settled recompute is also the trigger to save the
 * working draft, via the `saveDraft` passed in from `useValuationDraft`.
 *
 * VAL-INCOME-BASIS-FIX-1 — this hook never reconstructs the income leg and
 * `RecomputeBody` carries no `replacement_compensation` field of its own.
 * The wage deduction happens once, in Intake's `/run`; the result lands in
 * `body.income_value` (an already-computed, opaque figure) via the shared
 * draft (see `_valuationTuningHelpers.ts` D6 and `ValuationIntake.tsx`'s
 * post-run `saveDraft({ income_value: result.triangulation.income_value })`).
 * Because this hook forwards `body` verbatim to `/recompute` and `/preflight`
 * with no re-derivation, the Live preview sends the same wage-adjusted value
 * the run just produced — with nothing to wire here.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { preflightValuation, recomputeValuation } from '@/api/valuationB3'
import type {
  BasisBlock, ComputedPayload, RecomputeBody, SetAsidePreview, ValueMethodsPayload,
} from '@/types/valuationB3'
import type { SaveDraftBody } from '@/types/valuationDraft'

/** The preview's computed figures, with POL-A's `set_aside` (and M-DEMO's
 * screen-only `value_methods`) riding along. */
export type PreviewComputed = ComputedPayload & { set_aside?: SetAsidePreview; value_methods?: ValueMethodsPayload }

export interface UseTuningPreview {
  computed: PreviewComputed | null
  basis: BasisBlock | null
  blockers: string[]
  warnings: string[]
  runError: string | null
  /** VAL-NAV-STATE-1 R3.11 — the working draft's save is now awaited (below)
   * instead of fired-and-forgotten; this is its error, for the screen to
   * surface. Distinct from `runError`, which is the engine's, not the
   * draft's. */
  saveError: string | null
  pending: boolean
  /** M-DEMO — re-run the last preview (a saved cash / debt / growth rate changes `value_methods`). */
  refresh: () => void
}

/** OWNER_APPROVALS.log 2026-10-22 14:45 (P7B), verbatim. */
export const RATES_MISSING_MESSAGE =
  'Fill in the risk-free rate, company-specific risk, long-term growth rate and DCF growth rate above first.'

// WALKFIX-A F9 — the income engine's own refusals (core/valuation/income_leg.py
// IncomeLegError) name its fields in snake_case; the screen shows a plain
// sentence instead. The engine message itself stays in the server's log.
// Matched on the message's fixed opening, which income_leg.py owns.
// WF-A-2 / WF-A-3: owner-approved, verbatim (OWNER_APPROVALS.log 2026-10-25 11:15 CT).
const INCOME_LEG_PLAIN: [string, string][] = [
  ['replacement_compensation required',
    'To value the business on its earnings, we need the yearly salary it would cost to hire someone to run it. ' +
      'Enter it on Methodology and rates.'],
  ['replacement_compensation must be less than',
    'The salary to hire someone to run the business must be less than the business earnings.'],
]

export function plainIncomeLegMessage(message: string): string {
  const hit = INCOME_LEG_PLAIN.find(([opening]) => message.startsWith(opening))
  return hit ? hit[1] : message
}

function draftPatchFrom(payload: RecomputeBody): SaveDraftBody {
  return {
    benchmark_key: payload.benchmark_key,
    // VAL-PATH-SIGNER P7b — normalized_earnings is never saved from here:
    // the server sets it from Apply, the basis choice and the override.
    driver_answers: payload.driver_answers,
    naics_code: payload.naics_code,
    sub_vertical: payload.sub_vertical,
    market_only_acknowledged: payload.market_only_acknowledged,
    // VAL-INPUT-PERSIST-1E — always sent, so Reset (no knobs, no reason)
    // clears the saved tuning; a reason alone is kept as {rationale}.
    tuning: payload.tuning ?? (payload.rationale ? { rationale: payload.rationale } : null),
  }
}

export function useTuningPreview(
  id: string | undefined,
  ready: boolean,
  body: RecomputeBody | null,
  // VAL-NAV-STATE-1 R3.11 — `useValuationDraft`'s save() resolves the error
  // message on failure (was Promise<void>); this hook now awaits it and
  // exposes the result as `saveError` instead of firing it and forgetting.
  saveDraft: (body: SaveDraftBody) => Promise<string | null>,
  // VAL-INTAKE-BLANK-JUDGMENT-1 C2 — a rate is blank on Intake: ask the engine
  // for nothing, still save the working draft.
  ratesMissing = false,
): UseTuningPreview {
  const [computed, setComputed] = useState<PreviewComputed | null>(null)
  const [basis, setBasis] = useState<BasisBlock | null>(null)
  const [blockers, setBlockers] = useState<string[]>([])
  const [warnings, setWarnings] = useState<string[]>([])
  const [runError, setRunError] = useState<string | null>(null)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [pending, setPending] = useState(false)

  const seq = useRef(0)
  const last = useRef<RecomputeBody | null>(null)
  const runPreview = useCallback(
    (payload: RecomputeBody) => {
      if (!id) return
      last.current = payload
      const mine = ++seq.current
      setPending(true)
      Promise.all([
        recomputeValuation(id, payload),
        preflightValuation(id, payload).catch(() => null),
      ])
        .then(async ([rc, pf]) => {
          if (mine !== seq.current) return // a newer keystroke already won
          const { valuation_engagement_id: _id, value_kind: _k, persisted: _p, ...rest } = rc
          setComputed(rest as PreviewComputed)
          setBasis({
            config_version: rc.config_version,
            config_source: rc.config_source,
            config_fingerprint: rc.config_fingerprint,
            tuned: rc.tuned,
            tuning_rationale_recorded: rc.tuning_rationale_recorded,
          })
          setBlockers(pf ? pf.blockers.map(plainIncomeLegMessage) : [])
          setWarnings(pf ? pf.warnings : [])
          setRunError(null)
          // D2 — save on a settled Recompute, not on every character; this
          // debounce is the same one gating the recompute call itself.
          // R3.11 — awaited so a save failure is known, not silently lost.
          const err = await saveDraft(draftPatchFrom(payload))
          if (mine === seq.current) setSaveError(err)
        })
        .catch(err => {
          if (mine !== seq.current) return
          const message: string | undefined = err?.response?.data?.error?.message
          setRunError(
            message ? plainIncomeLegMessage(message) : 'The engine refused these inputs. Adjust and try again.',
          )
        })
        .finally(() => {
          if (mine === seq.current) setPending(false)
        })
    },
    [id, saveDraft],
  )

  useEffect(() => {
    if (!ready || !body) return
    if (ratesMissing) {
      const t = window.setTimeout(async () => {
        const err = await saveDraft(draftPatchFrom(body))
        setSaveError(err)
      }, 350)
      return () => window.clearTimeout(t)
    }
    const t = window.setTimeout(() => runPreview(body), 350)
    return () => window.clearTimeout(t)
  }, [ready, body, runPreview, ratesMissing, saveDraft])

  const refresh = useCallback(() => { if (last.current) runPreview(last.current) }, [runPreview])

  return {
    computed, basis, blockers, warnings, saveError, pending, refresh,
    runError: ready && ratesMissing ? RATES_MISSING_MESSAGE : runError,
  }
}
